import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MakeModel, Model, SelectableOption } from '@app-services/open.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SessionService } from '@app/store/session/session.service';
import { UserInfo } from '@app/store/session/session.store';
import { SegmentLabel } from '@app-model/vehicle';
import { EvalLeadFilter, EvalLeadReasonArchive, EvalLeadStatusLabels } from '@app-model/eval-lead';
import { EvalLeadService } from '@app-services/eval-lead.service';
import { City } from '@app-model/city';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { EvalLeadReason } from '@app/views/public/plugin-raio-x/plugin-raiox-lead.model';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import dayjs, { Dayjs } from 'dayjs';

@Component({
  selector: 'app-filter-eval-leads',
  templateUrl: './filter-eval-leads.component.html',
  styleUrls: ['./filter-eval-leads.component.scss']
})
export class FilterEvalLeadsComponent implements OnInit, OnChanges {
  @Input() filter: EvalLeadFilter;
  @Input() showFinishedStatus: boolean;
  @Output() filterChanged: EventEmitter<EvalLeadFilter> = new EventEmitter();

  userInfo: UserInfo;

  statusOptions: SelectableOption<EvalLeadStatusLabels>[];
  reasonOptions: SelectableOption<EvalLeadReason>[];
  reasonArchiveOptions: SelectableOption<EvalLeadReason>[];

  allSegments = SegmentLabel.allSegments;

  selectedDateRange: { startDate: Dayjs | null; endDate: Dayjs | null } | null = {
    startDate: dayjs().startOf('month'),
    endDate: dayjs(),
  };

  private isPeriodCleared = false;

  readonly ranges: Record<string, [Dayjs, Dayjs]> = {
    'Hoje': [dayjs().startOf('day'), dayjs().endOf('day')],
    'Ontem': [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
    'Últimos 7 dias': [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
    'Últimos 30 dias': [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')],
    'Mês atual': [dayjs().startOf('month'), dayjs().endOf('day')],
    'Mês passado': [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  };

  @ViewChild(NgxDaterangepickerBootstrapDirective) private readonly periodPicker?: NgxDaterangepickerBootstrapDirective;

  get hasPeriodSelected(): boolean {
    return !!(this.selectedDateRange?.startDate && this.selectedDateRange?.endDate);
  }

  makeModels: MakeModel[] = [];
  makeAutoCompleteControl = new FormControl();
  makeOptions: Observable<MakeModel[]>;
  selectedMakes: MakeModel[] = [];

  models: Model[] = [];
  modelAutoCompleteControl = new FormControl();
  modelOptions: Observable<Model[]>;
  selectedModels: Model[] = [];

  yearOptions: number[] = [];

  cityControl: FormControl = new FormControl();
  cityOptions: Observable<City[]>;

  constructor(
    private sessionService: SessionService,
    private evalLeadService: EvalLeadService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] && !changes['filter'].firstChange) {
      this.initStatusOptions();
      this.initReasonOptions();
      this.initReasonArchiveOptions();
      this.checkIfThereIsDate();
    }
  }

  ngOnInit() {
    this.sessionService.getUser().subscribe(u => {
      this.userInfo = u;
      this.initFilterOptions();
    });

    this.checkIfThereIsDate();
  }

  private initFilterOptions(): void {
    this.initStatusOptions();
    this.initReasonOptions();
    this.initReasonArchiveOptions();

    this.cityControl.valueChanges.subscribe((val) =>
      this.onChangeCityQuery(val)
    );

    this.evalLeadService
      .getFilterOptions(this.filter)
      .subscribe((options) => {
        if (!options || !options.minYear) {
          options.minYear = 2000;
        }
        const maxYear = dayjs().year() + 1;
        this.yearOptions.length = 0;
        for (let i = maxYear; i >= options.minYear; i--) {
          this.yearOptions.push(i);
        }

        this.makeModels = options.makeModels;
        this.makeOptions = this.makeAutoCompleteControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            if (value && typeof value === 'string') {
              value = value.toLowerCase();
            } else {
              value = '';
            }

            return this.makeModels.filter((option) =>
              option.name.toLowerCase().includes(value)
            );
          })
        );

        if (
          this.filter.makeIds &&
          this.filter.makeIds.length > 0 &&
          this.selectedMakes.length === 0
        ) {
          this.selectedMakes = this.makeModels.filter(
            (m) => this.filter.makeIds.indexOf(m.id) >= 0
          );
        }

        this.models = [];
        this.makeModels.forEach(
          (m) => (this.models = this.models.concat(m.models))
        );
        this.models.sort((m1, m2) => m1.name.localeCompare(m2.name));
        this.modelOptions = this.modelAutoCompleteControl.valueChanges.pipe(
          startWith(''),
          map((value) => {
            if (value && typeof value === 'string') {
              value = value.toLowerCase();
            } else {
              value = "";
            }
            return this.models.filter(
              (option) => option.name.toLowerCase().includes(value) &&
                this.isInSelectedMakes(option)
            );
          })
        );

        if (
          this.filter.modelIds &&
          this.filter.modelIds.length > 0 &&
          this.selectedModels.length === 0
        ) {
          this.selectedModels = this.models.filter(
            (m) => this.filter.modelIds.indexOf(m.id) >= 0
          );
        }
      });
  }


  onChangeCityQuery(value: string | City) {
    if (typeof value !== 'string') {
      this.filter.cityId = value.id;
      this.cityControl.setValue(value.name + ' - ' + value.uf);
    } else {
      this.cityOptions = this.evalLeadService.searchCitiesByName(
        value,
        this.filter
      );
    }
  }

  selectCity(city: City) {
    this.filter.cityId = city.id;
    this.submitFilter();
  }

  clearCity(): void {
    this.filter.cityId = undefined;
    this.submitFilter();
  }


  updateStatus(option: EvalLeadStatusLabels, event: MatCheckboxChange): void {
    if (!this.filter.statuses) {
      this.filter.statuses = [];
    }
    const key = this.getEnumKey(option, EvalLeadStatusLabels);
    if (event.checked && this.filter.statuses.indexOf(key) < 0) {
      this.filter.statuses.push(key);
    }

    if (!event.checked) {
      this.filter.statuses = this.filter.statuses.filter(f => f !== key);
    }
    this.submitFilter();
  }

  updateReason(option: EvalLeadReason, event: MatCheckboxChange): void {
    if (!this.filter.reasons) {
      this.filter.reasons = [];
    }
    const key = this.getEnumKey(option, EvalLeadReason);
    if (event.checked && this.filter.reasons.indexOf(key) < 0) {
      this.filter.reasons.push(key);
    }

    if (!event.checked) {
      this.filter.reasons = this.filter.reasons.filter(f => f !== key);
    }
    this.submitFilter();
  }

  updateReasonArchive(option: EvalLeadReasonArchive, event: MatCheckboxChange): void {
    if (!this.filter.reasonsArchives) {
      this.filter.reasonsArchives = [];
    }
    const key = this.getEnumKey(option, EvalLeadReasonArchive);
    if (event.checked && this.filter.reasonsArchives.indexOf(key) < 0) {
      this.filter.reasonsArchives.push(key);
    }

    if (!event.checked) {
      this.filter.reasonsArchives = this.filter.reasonsArchives.filter(f => f !== key);
    }
    this.submitFilter();
  }

  private initStatusOptions() {
    this.statusOptions = [];
    Object.keys(EvalLeadStatusLabels).forEach(key => {
      this.addEnumOption(
        this.statusOptions,
        this.filter.statuses,
        EvalLeadStatusLabels[key],
        EvalLeadStatusLabels
      );
    });
  }

  private initReasonOptions() {
    this.reasonOptions = [];
    Object.keys(EvalLeadReason).forEach(key => {
      this.addEnumOption(
        this.reasonOptions,
        this.filter.reasons,
        EvalLeadReason[key],
        EvalLeadReason
      );
    });
  }

  private initReasonArchiveOptions(){
    this.reasonArchiveOptions = [];
    Object.keys(EvalLeadReasonArchive).forEach(key => {
      this.addEnumOption(
        this.reasonArchiveOptions,
        this.filter.reasonsArchives,
        EvalLeadReasonArchive[key],
        EvalLeadReasonArchive
      )
    })
  }

  private addEnumOption(
    selectableOptions: SelectableOption<any>[],
    selectedOptions: string[],
    option: any,
    enumType: any
  ): void {
    const selectableOption = new SelectableOption<any>();
    selectableOption.option = option;
    selectableOption.selected =
      selectedOptions && selectedOptions.indexOf(this.getEnumKey(option, enumType)) >= 0;
    selectableOptions.push(selectableOption);
  }

  submitFilter(): void {
    this.filterChanged.emit(this.filter);
  }

  private getEnumKey(enumValue: any, enumType: any): string {
    const idx = Object.values(enumType).indexOf(enumValue);
    return Object.keys(enumType)[idx];
  }

  changeSegment(newSegment: string): void {
    if (!this.filter.segments) {
      this.filter.segments = [];
    }

    if (this.filter.segments.indexOf(newSegment) >= 0) {
      this.filter.segments = this.filter.segments.filter(s => s !== newSegment);
    } else {
      this.filter.segments.push(newSegment);
    }
    this.submitFilter();
    this.initFilterOptions();
  }


  private isInSelectedMakes(model: Model): boolean {
    if (this.selectedMakes === undefined || this.selectedMakes.length === 0) {
      return true;
    }
    for (const make of this.selectedMakes) {
      if (make.models.indexOf(model) >= 0) {
        return true;
      }
    }
    return false;
  }

  getMakeName(makeModel?: MakeModel): string | undefined {
    return makeModel ? makeModel.name : undefined;
  }

  selectedMake(event: MatAutocompleteSelectedEvent): void {
    const selectedMake = event.option.value;
    if (this.filter.makeIds === undefined) {
      this.filter.makeIds = [];
    }
    if (this.selectedMakes.indexOf(selectedMake) < 0) {
      this.selectedMakes.push(selectedMake);
      this.filter.makeIds.push(selectedMake.id);
      this.submitFilter();
    }
    this.makeAutoCompleteControl.setValue('');
  }

  removeSelectedMake(make: MakeModel): void {
    this.selectedMakes = this.selectedMakes.filter((m) => m.id !== make.id);
    this.filter.makeIds = this.filter.makeIds.filter((id) => id !== make.id);
    this.submitFilter();
  }

  getModelName(model?: Model): string | undefined {
    return model ? model.name : undefined;
  }

  selectedModel(event: MatAutocompleteSelectedEvent): void {
    const selectedModel = event.option.value;
    if (this.filter.modelIds === undefined) {
      this.filter.modelIds = [];
    }
    if (this.selectedModels.indexOf(selectedModel) < 0) {
      this.selectedModels.push(selectedModel);
      this.filter.modelIds.push(selectedModel.id);
      this.submitFilter();
    }
    this.modelAutoCompleteControl.setValue('');
  }

  removeSelectedModel(model: Model): void {
    this.selectedModels = this.selectedModels.filter((m) => m.id !== model.id);
    this.filter.modelIds = this.filter.modelIds.filter((id) => id !== model.id);
    this.submitFilter();
  }


  get minYearOptions(): number[] {
    return this.yearOptions.filter(
      (y) =>
        this.filter.modelYearEnd === undefined || y <= this.filter.modelYearEnd
    );
  }

  get maxYearOptions(): number[] {
    return this.yearOptions.filter(
      (y) =>
        this.filter.modelYearStart === undefined ||
        y >= this.filter.modelYearStart
    );
  }

  getReasonLabel(key: string): string {
    return EvalLeadReason[key];
  }

  clearPeriodRange(): void {
    this.isPeriodCleared = true;
    this.filter.createdAtIni = undefined;
    this.filter.createdAtEnd = undefined;
    this.filter.pageControl.page = 0;
    this.selectedDateRange = null;
    this.periodPicker?.clear();
    this.submitFilter();
  }

  selectedDate(event: { startDate?: Dayjs | null; endDate?: Dayjs | null }): void {
    if (!event?.startDate || !event?.endDate) {
      return;
    }
    this.isPeriodCleared = false;
    this.setRange(event.startDate, event.endDate);
    this.checkIfThereIsDate();
    this.submitFilter();
  }

  setRange(start: Dayjs, end: Dayjs): void {
    this.filter.createdAtIni = start.startOf('day').format();
    this.filter.createdAtEnd = end.startOf('day').format();

    this.filter.pageControl.page = 0;
  }

  private checkIfThereIsDate(): void {
    if (this.filter?.createdAtIni && this.filter?.createdAtEnd) {
      const startDate = dayjs(this.filter.createdAtIni);
      const endDate = dayjs(this.filter.createdAtEnd);
      this.selectedDateRange = { startDate, endDate };
      this.isPeriodCleared = false;
    } else if (this.isPeriodCleared) {
      this.selectedDateRange = null;
    } else {
      const startDate = dayjs().startOf('month');
      const endDate = dayjs();
      this.selectedDateRange = { startDate, endDate };
      this.setRange(startDate, endDate);
    }
  }
}
